'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { TypeAnimation } from 'react-type-animation'

// Academic cap — Graduation / Excellence
const CapIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
)

// Open book — Knowledge
const BookIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
)

// Globe — Pan-African scope
const GlobeIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </svg>
)

const MODES = [
  { id: 'cap', component: CapIcon },
  { id: 'book', component: BookIcon },
  { id: 'globe', component: GlobeIcon },
  { id: 'typing', component: null },
]

interface XEduLogoProps {
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showSlogan?: boolean
}

export function XEduLogo({ className = '', size = 'md', showSlogan = false }: XEduLogoProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const intervals = [3000, 3000, 3000, 6000]
    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % MODES.length)
    }, intervals[currentIndex])
    return () => clearTimeout(timer)
  }, [currentIndex])

  const currentMode = MODES[currentIndex]

  const sizeClasses = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-5xl',
    xl: 'text-7xl',
  }

  const iconSizes = {
    sm: 20,
    md: 28,
    lg: 44,
    xl: 60,
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className={`flex items-center gap-2 font-black tracking-tighter ${sizeClasses[size]}`}>
        {/* "X" en indigo */}
        <span className="text-[var(--color-xedu-indigo)]">X</span>

        {/* Icône animée */}
        <div className="relative inline-flex items-center justify-center min-h-[1em] min-w-[1em]">
          <AnimatePresence mode="wait">
            {currentMode.id === 'typing' ? (
              <motion.span
                key="typing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-[0.75em] whitespace-nowrap"
              >
                <TypeAnimation
                  sequence={[
                    'Edu', 1500,
                    ' by Djotta', 1500,
                  ]}
                  wrapper="span"
                  cursor={false}
                  repeat={0}
                />
              </motion.span>
            ) : (
              <motion.div
                key={currentMode.id}
                initial={{ scale: 0.5, opacity: 0, rotate: -180 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.5, opacity: 0, rotate: 180 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="inline-flex items-center justify-center text-foreground"
              >
                {currentMode.component && <currentMode.component size={iconSizes[size]} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* "Edu" — visible comme placeholder quand l'icône tourne */}
        {currentMode.id !== 'typing' && (
          <span className="text-[0.75em] opacity-0 pointer-events-none select-none whitespace-nowrap">
            Edu
          </span>
        )}
      </div>

      {showSlogan && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--color-xedu-indigo)]/60 mt-1 ml-1"
        >
          by Djotta
        </motion.p>
      )}
    </div>
  )
}
